/**
 * Progress milestone labels — Milestone 37.4.
 *
 * Progress should communicate confidence, not just a percentage. Each
 * brain surfaces a phase label alongside the number so the founder
 * feels traction, not toil.
 */
import type { BrainMissionType } from "./api";

export interface ProgressMilestone {
  percent: number;
  label: string;
  phase: string;
  detail: string;
}

/** Return the milestone label for a given completion percentage. */
export function milestoneFor(
  type: BrainMissionType,
  answered: number,
  total: number,
): ProgressMilestone {
  const percent = total === 0 ? 0 : Math.round((answered / total) * 100);
  const map = type === "business_brain" ? businessMilestones : brandMilestones;
  const match = [...map].reverse().find((m) => percent >= m.percent) ?? map[0];
  return { ...match, percent };
}

const businessMilestones: ProgressMilestone[] = [
  { percent: 0, label: "Getting Started", phase: "Getting Started", detail: "Every campaign Akinex builds starts here." },
  { percent: 15, label: "Warming Up", phase: "Warming Up", detail: "The foundation is beginning to take shape." },
  { percent: 33, label: "Foundation Forming", phase: "Foundation Forming", detail: "Akinex is starting to understand your business." },
  { percent: 50, label: "Foundation Established", phase: "Foundation Established", detail: "The essentials are in place. Every extra answer sharpens what comes next." },
  { percent: 75, label: "Depth Building", phase: "Depth Building", detail: "Akinex now has real substance to work with." },
  { percent: 90, label: "Nearly There", phase: "Nearly There", detail: "One or two more and you're ready for review." },
  { percent: 100, label: "Ready for Review", phase: "Ready for Review", detail: "Everything essential is captured. Time to approve." },
];

const brandMilestones: ProgressMilestone[] = [
  { percent: 0, label: "Getting Started", phase: "Getting Started", detail: "Every post Akinex writes starts here." },
  { percent: 15, label: "Voice Emerging", phase: "Voice Emerging", detail: "Your brand is starting to sound like itself." },
  { percent: 33, label: "Voice Forming", phase: "Voice Forming", detail: "Akinex is learning how to speak for you." },
  { percent: 50, label: "Voice Established", phase: "Voice Established", detail: "You now have a voice Akinex can carry consistently." },
  { percent: 75, label: "Signature Taking Shape", phase: "Signature Taking Shape", detail: "The details that make you unmistakable are settling in." },
  { percent: 90, label: "Nearly There", phase: "Nearly There", detail: "One or two more and your brand voice is ready." },
  { percent: 100, label: "Ready for Review", phase: "Ready for Review", detail: "Your voice is captured. Time to approve." },
];
